import React, { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Zap, TrendingUp, Users } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { track, trackLead, trackSliderDebounced } from "../../lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (n) =>
  n >= 1_000_000
    ? `$${(n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 1)}M`
    : n >= 1_000
      ? `$${(n / 1_000).toFixed(0)}K`
      : `$${n}`;

const fmtNum = (n) =>
  n >= 1_000 ? `${(n / 1_000).toFixed(n >= 10_000 ? 0 : 1)}K` : String(n);

export default function PipelineCalculator() {
  const [accounts, setAccounts] = useState(1200);
  const [acv, setAcv] = useState(85000);
  const [close, setClose] = useState(12);
  const [email, setEmail] = useState("");
  const [saving, setSaving] = useState(false);

  // Opinionated B2B benchmarks: ~18% MQL rate, ~25% MQL→SQL, close% user-controlled
  const { mqls, sqls, wins, arr } = useMemo(() => {
    const mqlRate = 0.18;
    const sqlRate = 0.25;
    const mqls = Math.round(accounts * mqlRate);
    const sqls = Math.round(mqls * sqlRate);
    const wins = Math.max(1, Math.round(sqls * (close / 100)));
    const arr = wins * acv;
    return { mqls, sqls, wins, arr };
  }, [accounts, acv, close]);

  const onChange = (name, setter) => (val) => {
    setter(val);
    trackSliderDebounced(name, val);
  };

  const emailIt = async () => {
    if (!email || !/^\S+@\S+\.\S+$/.test(email)) {
      toast.error("Enter a valid work email.");
      return;
    }
    setSaving(true);
    track("calculator_email_report_attempt");
    try {
      await axios.post(`${API}/leads`, {
        type: "calculator",
        email,
        payload: { accounts, acv, close, mqls, sqls, wins, arr },
      });
      track("calculator_email_report_success");
      trackLead("calculator", {
        accounts,
        acv,
        close,
        projected_arr: arr,
      });
      toast.success("Report on the way — we'll also follow up to tune this.");
      setEmail("");
    } catch (err) {
      track("calculator_email_report_error", { message: err?.message });
      toast.error("Something went wrong. Try again.");
    } finally {
      setSaving(false);
    }
  };

  const sliderStyle = (val, min, max) => {
    const pct = ((val - min) / (max - min)) * 100;
    return {
      background: `linear-gradient(to right, #FFB000 0%, #FFB000 ${pct}%, rgba(255,255,255,0.12) ${pct}%, rgba(255,255,255,0.12) 100%)`,
    };
  };

  return (
    <section
      id="calculator"
      data-testid="pipeline-calculator"
      className="section-dark py-section relative overflow-hidden"
    >
      <div className="absolute inset-0 opacity-40">
        <div className="absolute -top-20 left-1/4 w-[520px] h-[520px] rounded-full bg-[radial-gradient(circle,rgba(255,176,0,0.3),transparent_60%)] blur-3xl float-y-slow" />
        <div className="absolute bottom-0 right-1/4 w-[600px] h-[600px] rounded-full bg-[radial-gradient(circle,rgba(59,167,209,0.28),transparent_60%)] blur-3xl float-y" />
        <div className="absolute top-1/2 left-0 w-[420px] h-[420px] rounded-full bg-[radial-gradient(circle,rgba(233,106,127,0.2),transparent_60%)] blur-3xl" />
      </div>
      <div className="grain-overlay" />

      <div className="container-xl relative">
        <div className="max-w-3xl mb-12 md:mb-16 text-center mx-auto">
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#FFB000] mb-5">
            Pipeline calculator
          </div>
          <h2 className="font-display text-white text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[1.02] font-semibold">
            Your 12-month pipeline,{" "}
            <span className="font-serif italic font-light text-white/70">
              modelled live.
            </span>
          </h2>
          <p className="mt-5 text-white/65 text-base md:text-lg leading-relaxed">
            Move the sliders. Get a directional read on MQLs, SQLs, and ARR
            based on our benchmark conversion rates.
          </p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="grid grid-cols-1 lg:grid-cols-12 gap-5 items-stretch"
        >
          {/* Sliders */}
          <div className="lg:col-span-6 bg-white/[0.04] backdrop-blur-md border border-white/10 rounded-[28px] p-8 md:p-10 space-y-10">
            <Slider
              testId="calc-accounts"
              label="Target accounts (ICP)"
              value={accounts}
              min={100}
              max={5000}
              step={50}
              displayValue={fmtNum(accounts)}
              onChange={onChange("accounts", setAccounts)}
              style={sliderStyle(accounts, 100, 5000)}
            />
            <Slider
              testId="calc-acv"
              label="Average contract value (ACV)"
              value={acv}
              min={10000}
              max={500000}
              step={5000}
              displayValue={fmt(acv)}
              onChange={onChange("acv", setAcv)}
              style={sliderStyle(acv, 10000, 500000)}
            />
            <Slider
              testId="calc-close"
              label="SQL → Close rate"
              value={close}
              min={2}
              max={25}
              step={1}
              displayValue={`${close}%`}
              onChange={setClose}
              style={sliderStyle(close, 2, 25)}
            />

            <div className="pt-4 flex items-center gap-3 text-white/55 text-xs">
              <Zap size={14} strokeWidth={1.8} color="#FFB000" />
              <span>
                Benchmarks: 18% account → MQL · 25% MQL → SQL. Close% is yours.
              </span>
            </div>
          </div>

          {/* Output */}
          <div className="lg:col-span-6 relative rounded-[28px] p-8 md:p-10 overflow-hidden border border-white/10 bg-gradient-to-br from-[#FFB000]/[0.12] via-[#0A0A0A] to-[#3BA7D1]/[0.1]">
            <div className="absolute -top-24 -right-24 w-[380px] h-[380px] rounded-full bg-[radial-gradient(circle,rgba(255,176,0,0.35),transparent_60%)] blur-3xl" />
            <div className="relative flex flex-col h-full justify-between gap-8">
              <div>
                <div className="text-[11px] uppercase tracking-[0.22em] text-white/60 mb-4">
                  Projected 12-mo ARR
                </div>
                <motion.div
                  key={arr}
                  initial={{ opacity: 0.6, scale: 0.98 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.35, ease: "easeOut" }}
                  data-testid="calc-arr-output"
                  className="font-display text-white font-semibold tracking-tighter leading-none"
                  style={{
                    fontSize: "clamp(64px, 9vw, 148px)",
                    letterSpacing: "-0.05em",
                  }}
                >
                  <span className="text-[#FFB000]">{fmt(arr)}</span>
                </motion.div>
                <div className="mt-3 text-white/60 text-sm max-w-md leading-relaxed">
                  Directional estimate based on your inputs and our median
                  benchmarks. We&apos;ll tune this to your cycle in under 30
                  minutes.
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <OutputStat
                  testId="calc-mqls"
                  icon={Users}
                  label="MQLs / yr"
                  value={fmtNum(mqls)}
                  accent="#3BA7D1"
                />
                <OutputStat
                  testId="calc-sqls"
                  icon={TrendingUp}
                  label="SQLs / yr"
                  value={fmtNum(sqls)}
                  accent="#E96A7F"
                />
                <OutputStat
                  testId="calc-wins"
                  icon={Zap}
                  label="Closed deals"
                  value={String(wins)}
                  accent="#FFB000"
                />
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  type="email"
                  data-testid="calc-email-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="work@email.com"
                  className="flex-1 min-w-0 bg-white/10 border border-white/15 rounded-full px-5 py-3.5 text-sm text-white placeholder:text-white/40 focus:border-[#FFB000] outline-none"
                />
                <button
                  onClick={emailIt}
                  disabled={saving}
                  data-testid="calc-email-cta"
                  className="btn-pill btn-primary-amber text-[14px] px-6 disabled:opacity-60 shrink-0"
                >
                  {saving ? "Sending…" : "Email me this report"}
                  <ArrowUpRight size={15} strokeWidth={2.2} />
                </button>
              </div>
              <a
                href="/contact"
                data-testid="calc-cta"
                onClick={() => track("calculator_contact_cta_click")}
                className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white self-start"
              >
                Prefer a live call? Tune this with our team
                <ArrowUpRight size={14} strokeWidth={2.2} />
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function Slider({ testId, label, value, min, max, step, displayValue, onChange, style }) {
  return (
    <div>
      <div className="flex items-baseline justify-between mb-4">
        <label className="text-[11px] uppercase tracking-[0.22em] text-white/55">
          {label}
        </label>
        <div
          className="font-display text-white text-2xl md:text-3xl tracking-tight tabular-nums font-medium"
          data-testid={`${testId}-value`}
        >
          {displayValue}
        </div>
      </div>
      <input
        type="range"
        data-testid={testId}
        value={value}
        min={min}
        max={max}
        step={step}
        onChange={(e) => onChange(Number(e.target.value))}
        style={style}
        className="dc-range w-full"
      />
      <div className="flex justify-between mt-2 text-[10px] uppercase tracking-[0.18em] text-white/35">
        <span>{typeof min === "number" && min >= 1000 ? fmt(min) : min}</span>
        <span>{typeof max === "number" && max >= 1000 ? fmt(max) : max}</span>
      </div>
    </div>
  );
}

function OutputStat({ testId, icon: Icon, label, value, accent }) {
  return (
    <div
      data-testid={testId}
      className="bg-white/[0.04] rounded-[18px] p-4 md:p-5 border border-white/10"
    >
      <Icon size={16} strokeWidth={1.8} color={accent} className="mb-3" />
      <div
        className="font-display text-2xl md:text-3xl tracking-tight font-semibold text-white tabular-nums"
      >
        {value}
      </div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-white/50 mt-1">
        {label}
      </div>
    </div>
  );
}
